import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';

export type SmeDashboardFilterOptions = {
  dealNames: string[];
};

export type SmeDashboardFilters = {
  dealName: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). */
  asOfDate?: string;
};

export type SmeTapeDashboardKpiLatest = {
  totalPrincipalBalance: number;
  largestSingleObligorPct: number | null;
  largestSingleObligorName: string | null;
  largestSingleObligorDealName: string | null;
  top10ObligorConcentrationPct: number | null;
  facilityHhi: number | null;
  effectiveFacilityCount: number | null;
  facilityCount: number;
};

export type SmePaymentFrequencyLatestBucket = {
  bucket: string;
  latestVal: number;
};

export type SmeJurisdictionStateLatestRow = {
  state: string;
  latestVal: number;
};

export type SmeIndustryNaicsLatestRow = {
  industryNaics: string;
  latestVal: number;
};

export type SmeMaturityProfileLatestRow = {
  maturityYear: string;
  dealName: string;
  latestVal: number;
};

export type SmeMonthlyOriginationsRow = {
  /** Contract month as YYYY-MM. */
  contractMonth: string;
  productionUsd: number;
  loanCount: number;
};

const FILTER_OPTIONS_PATH = 'sme-dashboard/filter-options';

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseSmeKpiRow(raw: unknown): SmeTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;

  const totalPrincipalBalance = parseNum(o.totalPrincipalBalance ?? o.TotalPrincipalBalance);
  if (totalPrincipalBalance == null) {
    return null;
  }

  return {
    totalPrincipalBalance,
    largestSingleObligorPct: parseNum(o.largestSingleObligorPct ?? o.LargestSingleObligorPct),
    largestSingleObligorName: parseString(o.largestSingleObligorName ?? o.LargestSingleObligorName),
    largestSingleObligorDealName: parseString(o.largestSingleObligorDealName ?? o.LargestSingleObligorDealName),
    top10ObligorConcentrationPct: parseNum(o.top10ObligorConcentrationPct ?? o.Top10ObligorConcentrationPct),
    facilityHhi: parseNum(o.facilityHhi ?? o.FacilityHhi),
    effectiveFacilityCount: parseNum(o.effectiveFacilityCount ?? o.EffectiveFacilityCount),
    facilityCount: Math.trunc(parseNum(o.facilityCount ?? o.FacilityCount) ?? 0),
  };
}

function parseSmePaymentFrequencyLatest(raw: unknown): SmePaymentFrequencyLatestBucket[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.buckets ?? o.Buckets;
  if (!Array.isArray(list)) {
    return [];
  }
  const buckets: SmePaymentFrequencyLatestBucket[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const bucket = parseString(row.bucket ?? row.Bucket);
    const latestVal = parseNum(row.latestVal ?? row.LatestVal);
    if (!bucket || latestVal == null) {
      continue;
    }
    buckets.push({ bucket, latestVal });
  }
  return buckets;
}

function parseSmeJurisdictionStateLatest(raw: unknown): SmeJurisdictionStateLatestRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.states ?? o.States;
  if (!Array.isArray(list)) {
    return [];
  }
  const states: SmeJurisdictionStateLatestRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const state = parseString(row.state ?? row.State);
    const latestVal = parseNum(row.latestVal ?? row.LatestVal);
    if (!state || latestVal == null) {
      continue;
    }
    states.push({ state, latestVal });
  }
  return states;
}

function parseSmeIndustryNaicsLatest(raw: unknown): SmeIndustryNaicsLatestRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.industries ?? o.Industries;
  if (!Array.isArray(list)) {
    return [];
  }
  const industries: SmeIndustryNaicsLatestRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const industryNaics = parseString(row.industryNaics ?? row.IndustryNaics);
    const latestVal = parseNum(row.latestVal ?? row.LatestVal);
    if (!industryNaics || latestVal == null) {
      continue;
    }
    industries.push({ industryNaics, latestVal });
  }
  return industries;
}

function parseSmeMaturityProfileLatest(raw: unknown): SmeMaturityProfileLatestRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const rows: SmeMaturityProfileLatestRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const maturityYear = parseString(row.maturityYear ?? row.MaturityYear);
    const dealName = parseString(row.dealName ?? row.DealName);
    const latestVal = parseNum(row.latestVal ?? row.LatestVal);
    if (!maturityYear || !dealName || latestVal == null) {
      continue;
    }
    rows.push({ maturityYear, dealName, latestVal });
  }
  return rows;
}

function parseSmeMonthlyOriginations(raw: unknown): SmeMonthlyOriginationsRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const rows: SmeMonthlyOriginationsRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const contractMonth = parseString(row.contractMonth ?? row.ContractMonth);
    const productionUsd = parseNum(row.productionUsd ?? row.ProductionUsd);
    const loanCount = parseNum(row.loanCount ?? row.LoanCount);
    if (!contractMonth || productionUsd == null || loanCount == null) {
      continue;
    }
    rows.push({ contractMonth, productionUsd, loanCount });
  }
  return rows;
}

function appendSmeDashboardFilterParams(qs: URLSearchParams, filters: SmeDashboardFilters) {
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) {
      qs.append('dealName', t);
    }
  }

  const asOf = filters.asOfDate?.trim();
  if (asOf) {
    qs.set('asOfDate', asOf);
  }
}

export class SmeDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchSmeDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
    };
  };

  fetchSmeDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('sme-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchSmeTapeDashboardKpi = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `sme-dashboard/tape-kpi?${q}` : 'sme-dashboard/tape-kpi';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      return parseSmeKpiRow(JSON.parse(text) as unknown);
    }
    return parseSmeKpiRow(raw);
  };

  fetchSmePaymentFrequencyLatest = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `sme-dashboard/payment-frequency-latest?${q}` : 'sme-dashboard/payment-frequency-latest';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmePaymentFrequencyLatest(JSON.parse(text) as unknown);
    }
    return parseSmePaymentFrequencyLatest(raw);
  };

  fetchSmeJurisdictionStateLatest = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `sme-dashboard/jurisdiction-state-latest?${q}` : 'sme-dashboard/jurisdiction-state-latest';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmeJurisdictionStateLatest(JSON.parse(text) as unknown);
    }
    return parseSmeJurisdictionStateLatest(raw);
  };

  fetchSmeIndustryNaicsLatest = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `sme-dashboard/industry-naics-latest?${q}` : 'sme-dashboard/industry-naics-latest';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmeIndustryNaicsLatest(JSON.parse(text) as unknown);
    }
    return parseSmeIndustryNaicsLatest(raw);
  };

  fetchSmeMaturityProfileLatest = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `sme-dashboard/maturity-profile-latest?${q}` : 'sme-dashboard/maturity-profile-latest';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmeMaturityProfileLatest(JSON.parse(text) as unknown);
    }
    return parseSmeMaturityProfileLatest(raw);
  };

  fetchSmeOriginationVintageLatest = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `sme-dashboard/origination-vintage-latest?${q}` : 'sme-dashboard/origination-vintage-latest';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmeMaturityProfileLatest(JSON.parse(text) as unknown);
    }
    return parseSmeMaturityProfileLatest(raw);
  };

  fetchSmeMonthlyOriginations = async (filters: SmeDashboardFilters) => {
    const qs = new URLSearchParams();
    appendSmeDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `sme-dashboard/monthly-originations?${q}` : 'sme-dashboard/monthly-originations';

    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      return parseSmeMonthlyOriginations(JSON.parse(text) as unknown);
    }
    return parseSmeMonthlyOriginations(raw);
  };
}
